import { supabase } from './supabase';
import type { Game, Player, Settings, Winner } from './types';

// Game queries
export async function getGames() {
  const { data, error } = await supabase
    .from('games')
    .select(`
      *,
      game_players (
        player:players (*)
      )
    `)
    .order('game_date', { ascending: true });

  if (error) throw error;

  const games = data || [];

  // Fetch vote stats for all games
  const gameIds = games.map(g => g.id);
  const { data: allVoteStats } = gameIds.length > 0
    ? await supabase
        .from('game_votes_stats')
        .select('*')
        .in('game_id', gameIds)
    : { data: null };

  const voteStatsByGame: Record<string, NonNullable<typeof allVoteStats>> = {};
  for (const stat of allVoteStats || []) {
    const gid = stat.game_id;
    if (!voteStatsByGame[gid]) voteStatsByGame[gid] = [];
    voteStatsByGame[gid].push(stat);
  }

  return games.map(game => ({
    ...game,
    players: game.game_players.map((gp: any) => gp.player),
    results: (voteStatsByGame[game.id] || []).map(stat => ({
      playerName: stat.player_name,
      playerNumber: stat.player_number,
      voteCount: stat.vote_count,
      votePercentage: stat.vote_percentage || 0,
    })),
  }));
}

export async function getGame(id: string) {
  const { data: game, error: gameError } = await supabase
    .from('games')
    .select(`
      *,
      game_players (
        player:players (*)
      )
    `)
    .eq('id', id)
    .single();

  if (gameError) throw gameError;

  const { data: voteStats } = await supabase
    .from('game_votes_stats')
    .select('*')
    .eq('game_id', id);

  return {
    ...game,
    players: game.game_players.map((gp: any) => gp.player),
    results: voteStats?.map(stat => ({
      playerName: stat.player_name,
      playerNumber: stat.player_number,
      voteCount: stat.vote_count,
      votePercentage: stat.vote_percentage || 0,
    })) || [],
  };
}

export async function createGame(game: Omit<Game, 'id'>) {
  const { data, error: gameError } = await supabase
    .from('games')
    .insert({
      title: game.title,
      game_date: game.game_date,
      game_time: game.game_time,
      game_state: 'not_started',
      prismic_id: game.prismic_id ?? null,
    })
    .select()
    .single();

  if (gameError) throw gameError;

  if (game.players) {
    const { error: playersError } = await supabase
      .from('game_players')
      .insert(
        game.players.map(player => ({
          game_id: data.id,
          player_id: player.id,
        }))
      );

    if (playersError) throw playersError;
  }

  return data;
}

export async function updateGame(id: string, updates: Partial<Game>) {
  const { data: updatedGame, error: gameError } = await supabase
    .from('games')
    .update({
      title: updates.title,
      game_date: updates.game_date,
      game_time: updates.game_time,
      is_active: updates.is_active,
      voting_active: updates.voting_active,
      game_state: updates.game_state,
    })
    .eq('id', id)
    .select()
    .single();

  if (gameError) throw gameError;

  if (updates.players) {
    await supabase
      .from('game_players')
      .delete()
      .eq('game_id', id);

    const { error: playersError } = await supabase
      .from('game_players')
      .insert(
        updates.players.map(player => ({
          game_id: id,
          player_id: player.id,
        }))
      );

    if (playersError) throw playersError;
  }

  return updatedGame;
}

export async function deleteGame(id: string) {
  const { error } = await supabase
    .from('games')
    .delete()
    .eq('id', id);

  if (error) throw error;
}

// Player queries
export async function getPlayers() {
  const { data, error } = await supabase
    .from('players')
    .select('*')
    .order('number');

  if (error) throw error;
  return data || [];
}

export async function createPlayer(player: Omit<Player, 'id'>) {
  const { data, error } = await supabase
    .from('players')
    .insert(player)
    .select()
    .single();

  if (error) throw error;
  return data;
}

export async function updatePlayer(id: string, updates: Partial<Player>) {
  const { data, error } = await supabase
    .from('players')
    .update(updates)
    .eq('id', id)
    .select()
    .single();

  if (error) throw error;
  return data;
}

export async function deletePlayer(id: string) {
  const { error } = await supabase
    .from('players')
    .delete()
    .eq('id', id);

  if (error) throw error;
}

// Settings queries
const SETTINGS_COLUMNS =
  'sponsor_name, sponsor_image_url, collect_voter_details, voter_email_field, voter_name_field, voter_phone_field, voter_texts';

export async function getSettings() {
  const { data, error } = await supabase
    .from('settings')
    .select(SETTINGS_COLUMNS)
    .single();

  if (error) throw error;
  return data as Settings;
}

export async function updateSettings(updates: Partial<Settings>) {
  const { data, error } = await supabase
    .from('settings')
    .update({ ...updates, updated_at: new Date().toISOString() })
    .eq('id', true)
    .select(SETTINGS_COLUMNS)
    .single();

  if (error) throw error;
  return data as Settings;
}

// Vote queries
export async function getVoteByDeviceId(gameId: string, deviceId: string) {
  const { data, error } = await supabase
    .from('user_votes')
    .select('*')
    .eq('game_id', gameId)
    .eq('device_id', deviceId)
    .maybeSingle();

  if (error) throw error;
  return data;
}

// Prize draw queries

// PostgREST returns a to-one embed as an object, but older versions use an array
function one<T>(value: T | T[] | null): T | null {
  return Array.isArray(value) ? (value[0] ?? null) : value;
}

/**
 * The MVP is the player with the most votes. On a tie, every player sharing
 * the top spot counts as MVP.
 */
async function getMvpPlayerIds(gameId: string) {
  const { data, error } = await supabase
    .from('game_votes_stats')
    .select('player_id, vote_count')
    .eq('game_id', gameId);

  if (error) throw error;

  const maxVotes = Math.max(0, ...(data || []).map(s => s.vote_count));
  if (maxVotes === 0) return [];
  return (data || []).filter(s => s.vote_count === maxVotes).map(s => s.player_id as string);
}

/** Votes on the MVP from voters who left at least one personal detail */
async function getEligibleVoteIds(gameId: string) {
  const mvpPlayerIds = await getMvpPlayerIds(gameId);
  if (mvpPlayerIds.length === 0) return [];

  const { data, error } = await supabase
    .from('votes')
    .select('id, voter_details!inner(vote_id)')
    .eq('game_id', gameId)
    .in('player_id', mvpPlayerIds);

  if (error) throw error;
  return (data || []).map(v => v.id as string);
}

export async function getEligibleVoterCount(gameId: string) {
  return (await getEligibleVoteIds(gameId)).length;
}

export async function getWinners(gameId: string): Promise<Winner[]> {
  const { data, error } = await supabase
    .from('winners')
    .select(`
      vote_id,
      votes (
        players (
          name,
          number
        ),
        voter_details (
          email,
          name,
          phone
        )
      )
    `)
    .eq('game_id', gameId)
    .order('created_at');

  if (error) throw error;

  return (data || []).map((w: any) => {
    const vote = one(w.votes) as any;
    const player = one(vote?.players) as any;
    const details = one(vote?.voter_details) as any;
    return {
      vote_id: w.vote_id,
      email: details?.email ?? null,
      name: details?.name ?? null,
      phone: details?.phone ?? null,
      player_name: player?.name ?? '',
      player_number: player?.number ?? 0,
    };
  });
}

export async function selectWinners(gameId: string, count: number) {
  const voteIds = await getEligibleVoteIds(gameId);

  if (voteIds.length === 0) {
    throw new Error('Geen stemmers op de MVP met ingevulde gegevens');
  }

  // Fisher-Yates shuffle
  for (let i = voteIds.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [voteIds[i], voteIds[j]] = [voteIds[j], voteIds[i]];
  }

  const { error: deleteError } = await supabase
    .from('winners')
    .delete()
    .eq('game_id', gameId);

  if (deleteError) throw deleteError;

  const { error } = await supabase
    .from('winners')
    .insert(voteIds.slice(0, count).map(voteId => ({
      game_id: gameId,
      vote_id: voteId,
    })));

  if (error) throw error;

  // Voting pages listen to game updates and check whether they won
  const { error: gameError } = await supabase
    .from('games')
    .update({ winners_drawn_at: new Date().toISOString() })
    .eq('id', gameId);

  if (gameError) throw gameError;
}

export async function updateGameState(id: string, state: Game['game_state']) {
  const { data, error } = await supabase
    .from('games')
    .update({ game_state: state })
    .eq('id', id)
    .select()
    .single();

  if (error) throw error;
  return data;
}